import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import cn from 'bem-cn';

import { PromptStatus } from '@ai-platform/utils';
import { WebAppLayout } from '../../layouts/WebAppLayout';
import { NavigationElements } from '../../components/Navigation';
import { HeaderElements } from '../../components/Header/Header.types';
import { InfoBlock, Stack, Steps } from '../../components/UI';
import { Step, StepStatus } from '../../components/UI/Steps/Steps.types';

import { useGetPromptQuery } from '../../store';
import { useNavigateBack } from '../../hooks/useNavigateBack';
import { NewPromptForm } from '../../components/NewPromptForm';

import { InfoBlockProps } from '../../components/UI/InfoBlock';
import { mapCategoriesToEnglish } from '../../utils/categories';

import { CONDITIONS_URL, RoutePaths } from '../../constants';
import { getRandomPhrase, getPublicationStep, getModerationStepStatus } from './helpers';
import './NewPromptStatusPage.scss';

const b = cn('new-prompt-status-page');

export const NewPromptStatusPage = () => {
    const navigate = useNavigate();

    const { slug } = useParams();
    const promptId = Number(slug);

    const isValidPromptId = Boolean(promptId);
    const { data, isLoading, error } = useGetPromptQuery({ promptId }, { skip: !isValidPromptId });

    const { status: promptStatus, moderationComment } = data || {};

    useEffect(() => {
        if (promptStatus === PromptStatus.Published) {
            navigate(`${RoutePaths.Prompt}/${promptId}`);
        }
    }, [navigate, promptId, promptStatus]);

    const handleBackButtonClick = useNavigateBack();

    const getSteps = (): Step[] => {
        return [
            {
                text: 'Заявка',
                status: StepStatus.Done,
                icon: 'application',
            },
            {
                text: 'Модерация',
                status: getModerationStepStatus(promptStatus),
                icon: 'time',
            },
            getPublicationStep(promptStatus),
        ];
    };

    const emptyNavigationElements: NavigationElements = {
        desktop: [],
        mobile: [],
    };

    const renderHeaderText = () => {
        return <p className={b('header-text')}>Заявка</p>;
    };

    const headerElements: HeaderElements = {
        desktop: {
            left: {
                type: 'logo',
            },
            center: {
                type: 'custom',
                render: renderHeaderText,
            },
            right: {
                type: 'profile',
            },
        },
        mobile: {
            left: {
                type: 'backButton',
                onClick: handleBackButtonClick,
            },
            center: {
                type: 'custom',
                render: renderHeaderText,
            },
        },
    };

    const getInfoBlockProps = useCallback(() => {
        if (error) {
            return {
                icon: 'close',
                text: 'Что-то пошло не так',
            };
        }

        switch (promptStatus) {
            case PromptStatus.Moderation:
                return {
                    icon: 'like',
                    text: 'Заявка отправлена на модерацию',
                };
            case PromptStatus.Rejected:
                return {
                    icon: 'info',
                    text: `Заявка не соответствует <a href='${CONDITIONS_URL}'>правилам участия</a>`,
                    isError: true,
                };
            case PromptStatus.Postponed:
                return {
                    icon: 'time',
                    text: getRandomPhrase(moderationComment),
                    isWarning: true,
                };
            default: {
                return {
                    text: 'Загрузка...',
                };
            }
        }
    }, [promptStatus, error, moderationComment]);

    return (
        <WebAppLayout navigationElements={emptyNavigationElements} headerElements={headerElements}>
            <Stack className={b()}>
                <Steps steps={getSteps()} />
                <InfoBlock {...(getInfoBlockProps() as InfoBlockProps)} />
                {!isLoading && (
                    <NewPromptForm
                        promptText={data?.prompt || ''}
                        promptTitle={data?.title || ''}
                        promptProblem={data?.description || ''}
                        selectedCategories={mapCategoriesToEnglish(data?.categories || [])}
                        disabled
                    />
                )}
            </Stack>
        </WebAppLayout>
    );
};
